import type { GameDefinition, Outcome, Rng, Seat, Viewer } from '@campfire/engine';
import { rollDice } from '@campfire/dice';

export const START_DICE = 5;

export interface LiarsOptions {
  /** Ones count as any face (except in bids on ones themselves). */
  readonly wild: boolean;
}

export interface Bid {
  readonly seat: Seat;
  readonly qty: number;
  readonly face: number;
}

export interface Reveal {
  readonly kind: 'reveal';
  readonly bid: Bid;
  readonly caller: Seat;
  /** Every cup lifted, per seat. */
  readonly dice: readonly (readonly number[])[];
  readonly count: number;
  readonly holds: boolean;
  readonly loser: Seat;
  readonly eliminated: boolean;
}

export type LiarsEvent = { readonly kind: 'bid'; readonly bid: Bid } | Reveal;

export interface LiarsState {
  readonly options: LiarsOptions;
  readonly dice: readonly (readonly number[])[];
  readonly turn: Seat;
  readonly bid: Bid | null;
  readonly round: number;
  readonly last: LiarsEvent | null;
  readonly result: Outcome | null;
}

export type LiarsAction = { readonly type: 'bid'; readonly qty: number; readonly face: number } | { readonly type: 'liar' };

export interface LiarsView {
  readonly you: Seat | null;
  /** Your own dice; null for spectators (and never anyone else's). */
  readonly myDice: readonly number[] | null;
  readonly counts: readonly number[];
  readonly total: number;
  readonly turn: Seat;
  readonly bid: Bid | null;
  readonly round: number;
  readonly wild: boolean;
  readonly last: LiarsEvent | null;
  readonly result: Outcome | null;
}

const counts = (s: LiarsState): number[] => s.dice.map((d) => d.length);
const totalDice = (s: LiarsState): number => counts(s).reduce((a, b) => a + b, 0);

/** Dice matching a bid on `face` across all cups (ones wild when on, except for bids on ones). */
export function countFace(dice: readonly (readonly number[])[], face: number, wild: boolean): number {
  return dice.flat().filter((d) => d === face || (wild && d === 1 && face !== 1)).length;
}

/** A bid must raise the quantity, or keep it and raise the face. */
export const beats = (qty: number, face: number, prev: Bid | null): boolean => prev === null || qty > prev.qty || (qty === prev.qty && face > prev.face);

/** Next seat after `from` that still has dice; `sizes` are per-seat dice counts. */
function nextLive(sizes: readonly number[], from: Seat): Seat {
  for (let k = 1; k <= sizes.length; k++) {
    const s = (from + k) % sizes.length;
    if ((sizes[s] as number) > 0) return s;
  }
  return from;
}

const rollAll = (sizes: readonly number[], rng: Rng): number[][] => sizes.map((n) => rollDice(n, rng));

export const liarsDice: GameDefinition<LiarsState, LiarsAction, LiarsView, LiarsOptions> = {
  id: 'liars-dice',
  name: "Liar's Dice",
  rules: [
    'Everyone has a cup of 5 dice that only they can see. Be the last player with any dice.',
    'On your turn, bid how many dice of one face (1 to 6) are showing across all cups, or call the previous bid a lie.',
    'A bid must beat the last one: a higher quantity of any face, or the same quantity of a higher face. The quantity cannot be more than all the dice in play.',
    'When someone calls Liar, all dice are revealed and counted. If at least that many were there, the bid was good and the caller loses a die. If not, the bidder loses one.',
    'Everyone then rolls again, and the player who lost the die starts the next bid (if they were eliminated, the next player still in does).',
    'Setting: Ones. By default ones are wild and count toward every face, except when the bid is on ones. Choose Ordinary dice to turn that off.',
    'The first player to bid is seat 1. Players with no dice left drop out, and the last one standing wins.',
    'The app doesn’t have spot-on calls, palifico rounds or restricted bids. Add any of those as house rules if you like.',
  ],
  minSeats: 2,
  maxSeats: 6,
  defaultOptions: { wild: true },
  optionSpecs: [
    {
      key: 'wild',
      label: 'Ones',
      help: 'Wild ones count toward every face except bids on ones themselves.',
      choices: [
        { value: true, label: 'Wild' },
        { value: false, label: 'Ordinary dice' },
      ],
    },
  ],

  setup: ({ seats, options, rng }) => ({
    options,
    dice: rollAll(Array.from({ length: seats }, () => START_DICE), rng),
    turn: 0,
    bid: null,
    round: 1,
    last: null,
    result: null,
  }),

  activeSeats: (s) => (s.result ? [] : [s.turn]),

  validate(s, a, seat) {
    if (s.result) return { ok: false, reason: 'game over' };
    if (seat !== s.turn) return { ok: false, reason: 'not your turn' };
    if (a.type === 'liar') return s.bid ? { ok: true } : { ok: false, reason: 'there is no bid to challenge' };
    if (a.type !== 'bid') return { ok: false, reason: 'unknown action' };
    if (!Number.isInteger(a.qty) || !Number.isInteger(a.face) || a.face < 1 || a.face > 6 || a.qty < 1) return { ok: false, reason: 'bad bid' };
    if (a.qty > totalDice(s)) return { ok: false, reason: `only ${totalDice(s)} dice are in play` };
    return beats(a.qty, a.face, s.bid) ? { ok: true } : { ok: false, reason: 'raise the quantity, or keep it and raise the face' };
  },

  apply(s, a, { rng }) {
    const seat = s.turn;
    if (a.type === 'bid') {
      const bid: Bid = { seat, qty: a.qty, face: a.face };
      return { ...s, bid, turn: nextLive(counts(s), seat), last: { kind: 'bid', bid } };
    }
    const bid = s.bid as Bid;
    const count = countFace(s.dice, bid.face, s.options.wild);
    const holds = count >= bid.qty;
    const loser = holds ? seat : bid.seat;
    const sizes = counts(s).map((n, i) => (i === loser ? n - 1 : n));
    const eliminated = sizes[loser] === 0;
    const reveal: Reveal = { kind: 'reveal', bid, caller: seat, dice: s.dice, count, holds, loser, eliminated };
    const alive = sizes.flatMap((n, i) => (n > 0 ? [i] : []));
    if (alive.length === 1) {
      const winner = alive[0] as Seat;
      return {
        ...s,
        dice: s.dice.map((d, i) => d.slice(0, sizes[i])),
        bid: null,
        last: reveal,
        result: { winners: [winner], draw: false, reason: 'last with dice', scores: { ...sizes } },
      };
    }
    const starter = eliminated ? nextLive(sizes, loser) : loser;
    return { ...s, dice: rollAll(sizes, rng), bid: null, turn: starter, round: s.round + 1, last: reveal };
  },

  view(s, viewer: Viewer): LiarsView {
    const you = viewer.kind === 'seat' ? viewer.seat : null;
    return {
      you,
      myDice: you === null ? null : (s.dice[you] ?? null),
      counts: counts(s),
      total: totalDice(s),
      turn: s.turn,
      bid: s.bid,
      round: s.round,
      wild: s.options.wild,
      last: s.last,
      result: s.result,
    };
  },

  outcome: (s) => s.result,

  enumerateActions(s, seat) {
    if (s.result || seat !== s.turn) return [];
    const out: LiarsAction[] = s.bid ? [{ type: 'liar' }] : [];
    const total = totalDice(s);
    for (let qty = 1; qty <= total; qty++) {
      for (let face = 1; face <= 6; face++) if (beats(qty, face, s.bid)) out.push({ type: 'bid', qty, face });
    }
    return out;
  },
};
